/**
 * Onboarding Tour Definitions
 *
 * Declarative configuration for the guided tour.
 * Each chapter groups related steps by route/context.
 * Steps reference elements via data-tour attributes.
 */

import { OnboardingChapter } from '@/types';

// ============================================================================
// PHASE CONSTANTS
// ============================================================================

/** Chapter IDs that belong to Phase 1 (chat-first creation) */
export const PHASE1_CHAPTER_IDS = ['welcome-dashboard', 'navigation'];

/** Chapter IDs that belong to Phase 2 (editor) */
export const PHASE2_CHAPTER_IDS = ['editor-overview', 'editor-text', 'editor-images', 'editor-styling', 'export-conclusion'];

/** First chapter ID of Phase 2 */
export const PHASE2_FIRST_CHAPTER_ID = 'editor-overview';

/** Last chapter ID of Phase 1 */
export const PHASE1_LAST_CHAPTER_ID = 'navigation';

export const ONBOARDING_CHAPTERS: OnboardingChapter[] = [
  // ============================================================================
  // Chapter 1: Welcome & Dashboard
  // ============================================================================
  {
    id: 'welcome-dashboard',
    titleKey: 'onboarding.chapters.welcomeDashboard',
    route: '/',
    steps: [
      {
        id: 'chat-greeting',
        targetSelector: '[data-tour="chat-greeting"]',
        titleKey: 'onboarding.steps.chatGreeting.title',
        descriptionKey: 'onboarding.steps.chatGreeting.description',
        placement: 'bottom',
        spotlight: true,
      },
      {
        id: 'chat-input',
        targetSelector: '[data-tour="chat-input"]',
        titleKey: 'onboarding.steps.chatInput.title',
        descriptionKey: 'onboarding.steps.chatInput.description',
        placement: 'top',
        spotlight: true,
      },
      {
        id: 'chat-plus',
        targetSelector: '[data-tour="chat-plus-menu"]',
        titleKey: 'onboarding.steps.chatAdvancedOptions.title',
        descriptionKey: 'onboarding.steps.chatAdvancedOptions.description',
        placement: 'right',
        mobilePlacement: 'top',
        spotlight: true,
        beforeShowAction: 'open-chat-plus-menu',
      },
      {
        id: 'chat-suggestions',
        targetSelector: '[data-tour="chat-suggestions"]',
        titleKey: 'onboarding.steps.chatSuggestions.title',
        descriptionKey: 'onboarding.steps.chatSuggestions.description',
        placement: 'top',
        spotlight: true,
      },
    ],
  },

  // ============================================================================
  // Chapter 2: Navigation
  // ============================================================================
  {
    id: 'navigation',
    titleKey: 'onboarding.chapters.navigation',
    route: '/',
    steps: [
      {
        id: 'sidebar',
        targetSelector: '[data-tour="sidebar-nav"]',
        mobileTargetSelector: '[data-tour="mobile-menu-btn"]',
        titleKey: 'onboarding.steps.sidebar.title',
        descriptionKey: 'onboarding.steps.sidebar.description',
        placement: 'right',
        mobilePlacement: 'bottom',
        spotlight: true,
      },
      {
        id: 'settings',
        targetSelector: '[data-tour="sidebar-settings"]',
        titleKey: 'onboarding.steps.settings.title',
        descriptionKey: 'onboarding.steps.settings.description',
        placement: 'right',
        spotlight: true,
        desktopOnly: true,
        beforeShowAction: 'open-settings-flyout',
      },
      {
        id: 'language',
        targetSelector: '[data-tour="sidebar-language"]',
        titleKey: 'onboarding.steps.language.title',
        descriptionKey: 'onboarding.steps.language.description',
        placement: 'right',
        spotlight: true,
        desktopOnly: true,
        beforeShowAction: 'open-settings-flyout',
      },
      {
        id: 'theme',
        targetSelector: '[data-tour="sidebar-theme"]',
        titleKey: 'onboarding.steps.theme.title',
        descriptionKey: 'onboarding.steps.theme.description',
        placement: 'right',
        spotlight: true,
        desktopOnly: true,
      },
    ],
  },

  // ============================================================================
  // Chapter 4: Editor - Overview
  // ============================================================================
  {
    id: 'editor-overview',
    titleKey: 'onboarding.chapters.editorOverview',
    route: '/editor/:id',
    steps: [
      {
        id: 'editor-intro',
        targetSelector: '',
        titleKey: 'onboarding.steps.editorIntro.title',
        descriptionKey: 'onboarding.steps.editorIntro.description',
        placement: 'center',
        spotlight: false,
      },
      {
        id: 'editor-slide-list',
        targetSelector: '[data-tour="editor-slide-list"]',
        mobileTargetSelector: '[data-tour="mobile-tab-slides"]',
        titleKey: 'onboarding.steps.editorSlideList.title',
        descriptionKey: 'onboarding.steps.editorSlideList.description',
        placement: 'right',
        mobilePlacement: 'top',
        spotlight: true,
      },
      {
        id: 'editor-preview',
        targetSelector: '[data-tour="editor-preview"]',
        mobileTargetSelector: '[data-tour="editor-preview-mobile"]',
        titleKey: 'onboarding.steps.editorPreview.title',
        descriptionKey: 'onboarding.steps.editorPreview.description',
        placement: 'bottom',
        spotlight: true,
      },
      {
        id: 'editor-zoom',
        targetSelector: '[data-tour="editor-zoom"]',
        titleKey: 'onboarding.steps.editorZoom.title',
        descriptionKey: 'onboarding.steps.editorZoom.description',
        placement: 'bottom',
        spotlight: true,
        desktopOnly: true,
      },
      {
        id: 'editor-right-panel',
        targetSelector: '[data-tour="editor-right-panel"]',
        mobileTargetSelector: '[data-tour="mobile-tab-edit"]',
        titleKey: 'onboarding.steps.editorRightPanel.title',
        descriptionKey: 'onboarding.steps.editorRightPanel.description',
        placement: 'left',
        mobilePlacement: 'top',
        spotlight: true,
      },
    ],
  },

  // ============================================================================
  // Chapter 5: Editor - Text & Content
  // ============================================================================
  {
    id: 'editor-text',
    titleKey: 'onboarding.chapters.editorText',
    route: '/editor/:id',
    steps: [
      {
        id: 'editor-sidebar-toggle',
        targetSelector: '[data-tour="editor-sidebar-toggle"]',
        titleKey: 'onboarding.steps.editorSidebarToggle.title',
        descriptionKey: 'onboarding.steps.editorSidebarToggle.description',
        placement: 'bottom',
        spotlight: true,
        desktopOnly: true,
      },
      {
        id: 'editor-text-editor',
        targetSelector: '[data-tour="editor-text-editor"]',
        mobileTargetSelector: '[data-tour="mobile-text-editor"]',
        titleKey: 'onboarding.steps.editorTextEditor.title',
        descriptionKey: 'onboarding.steps.editorTextEditor.description',
        placement: 'left',
        mobilePlacement: 'top',
        spotlight: true,
        beforeShowAction: 'switch-to-slide-tab',
      },
      {
        id: 'editor-add-slide',
        targetSelector: '[data-tour="editor-add-slide"]',
        mobileTargetSelector: '[data-tour="editor-add-slide-mobile"]',
        titleKey: 'onboarding.steps.editorAddSlide.title',
        descriptionKey: 'onboarding.steps.editorAddSlide.description',
        placement: 'top',
        spotlight: true,
        beforeShowAction: 'switch-to-mobile-slides',
      },
    ],
  },

  // ============================================================================
  // Chapter 6: Editor - Images & AI
  // ============================================================================
  {
    id: 'editor-images',
    titleKey: 'onboarding.chapters.editorImages',
    route: '/editor/:id',
    steps: [
      {
        id: 'editor-image-section',
        targetSelector: '[data-tour="editor-image-section"]',
        mobileTargetSelector: '[data-tour="mobile-tab-media"]',
        titleKey: 'onboarding.steps.editorImageSection.title',
        descriptionKey: 'onboarding.steps.editorImageSection.description',
        placement: 'left',
        mobilePlacement: 'top',
        spotlight: true,
        beforeShowAction: 'switch-to-slide-tab',
      },
      {
        id: 'editor-image-generate',
        targetSelector: '[data-tour="editor-image-generate"]',
        titleKey: 'onboarding.steps.editorImageGenerate.title',
        descriptionKey: 'onboarding.steps.editorImageGenerate.description',
        placement: 'left',
        spotlight: true,
        desktopOnly: true,
        beforeShowAction: 'enable-image-generate',
      },
      {
        id: 'editor-batch-mode',
        targetSelector: '[data-tour="editor-batch-mode"]',
        mobileTargetSelector: '[data-tour="editor-batch-mode-mobile"]',
        titleKey: 'onboarding.steps.editorBatchMode.title',
        descriptionKey: 'onboarding.steps.editorBatchMode.description',
        placement: 'bottom',
        spotlight: true,
        beforeShowAction: 'switch-to-mobile-slides',
      },
    ],
  },

  // ============================================================================
  // Chapter 7: Editor - Styling
  // ============================================================================
  {
    id: 'editor-styling',
    titleKey: 'onboarding.chapters.editorStyling',
    route: '/editor/:id',
    steps: [
      {
        id: 'editor-theme-toggle',
        targetSelector: '[data-tour="editor-theme-toggle"]',
        mobileTargetSelector: '[data-tour="mobile-theme-toggle"]',
        titleKey: 'onboarding.steps.editorThemeToggle.title',
        descriptionKey: 'onboarding.steps.editorThemeToggle.description',
        placement: 'left',
        mobilePlacement: 'top',
        spotlight: true,
        beforeShowAction: 'switch-to-global-tab',
      },
      {
        id: 'editor-font-select',
        targetSelector: '[data-tour="editor-font-select"]',
        titleKey: 'onboarding.steps.editorFontSelect.title',
        descriptionKey: 'onboarding.steps.editorFontSelect.description',
        placement: 'left',
        spotlight: true,
        desktopOnly: true,
        beforeShowAction: 'switch-to-global-tab',
      },
      {
        id: 'editor-accent-color',
        targetSelector: '[data-tour="editor-accent-color"]',
        titleKey: 'onboarding.steps.editorAccentColor.title',
        descriptionKey: 'onboarding.steps.editorAccentColor.description',
        placement: 'left',
        spotlight: true,
        desktopOnly: true,
        beforeShowAction: 'switch-to-global-tab',
      },
    ],
  },

  // ============================================================================
  // Chapter 8: Export & Conclusion
  // ============================================================================
  {
    id: 'export-conclusion',
    titleKey: 'onboarding.chapters.exportConclusion',
    route: '/editor/:id',
    steps: [
      {
        id: 'editor-export',
        targetSelector: '[data-tour="editor-export"]',
        titleKey: 'onboarding.steps.editorExport.title',
        descriptionKey: 'onboarding.steps.editorExport.description',
        placement: 'bottom',
        spotlight: true,
      },
      {
        id: 'conclusion',
        targetSelector: '',
        titleKey: 'onboarding.steps.conclusion.title',
        descriptionKey: 'onboarding.steps.conclusion.description',
        placement: 'center',
        spotlight: false,
      },
    ],
  },
];

/**
 * Get the total number of steps across all chapters.
 */
export function getTotalSteps(): number {
  return ONBOARDING_CHAPTERS.reduce((sum, ch) => sum + ch.steps.length, 0);
}

/**
 * Get the global index of a step (across all chapters).
 */
export function getGlobalStepIndex(chapterId: string, stepIndex: number): number {
  let count = 0;
  for (const chapter of ONBOARDING_CHAPTERS) {
    if (chapter.id === chapterId) return count + stepIndex;
    count += chapter.steps.length;
  }
  return count;
}

/**
 * Find a chapter by its ID.
 */
export function findChapter(chapterId: string) {
  return ONBOARDING_CHAPTERS.find((ch) => ch.id === chapterId);
}

/**
 * Get chapter index by ID.
 */
export function getChapterIndex(chapterId: string): number {
  return ONBOARDING_CHAPTERS.findIndex((ch) => ch.id === chapterId);
}

// ============================================================================
// PHASE-AWARE HELPERS
// ============================================================================

/**
 * Check if a chapter belongs to Phase 2.
 */
export function isPhase2Chapter(chapterId: string): boolean {
  return PHASE2_CHAPTER_IDS.includes(chapterId);
}

/**
 * Get chapters for a given phase.
 */
export function getPhaseChapters(phase: 'phase1' | 'waiting' | 'phase2'): OnboardingChapter[] {
  const ids = phase === 'phase2' ? PHASE2_CHAPTER_IDS : PHASE1_CHAPTER_IDS;
  return ONBOARDING_CHAPTERS.filter((ch) => ids.includes(ch.id));
}

/**
 * Get the total number of steps for a given phase.
 */
export function getPhaseTotalSteps(phase: 'phase1' | 'waiting' | 'phase2'): number {
  return getPhaseChapters(phase).reduce((sum, ch) => sum + ch.steps.length, 0);
}

/**
 * Get the global step index within a phase.
 */
export function getPhaseGlobalStepIndex(
  phase: 'phase1' | 'waiting' | 'phase2',
  chapterId: string,
  stepIndex: number,
): number {
  let count = 0;
  for (const chapter of getPhaseChapters(phase)) {
    if (chapter.id === chapterId) return count + stepIndex;
    count += chapter.steps.length;
  }
  return count;
}

/**
 * Get the chapter index within a phase.
 */
export function getPhaseChapterIndex(
  phase: 'phase1' | 'waiting' | 'phase2',
  chapterId: string,
): number {
  return getPhaseChapters(phase).findIndex((ch) => ch.id === chapterId);
}
